'use client';

import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyboardArrowDown, KeyboardArrowUp } from '@lichtblick/icons';

import {
  AutocompleteButton,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteOption,
  AutocompleteOptions,
  AutocompleteTrigger,
  ChipContainer,
  Container,
  OptionsContainer,
} from './AutocompleteMultiselect.styles';

import { Chip } from '../Chip';

import type { Option } from '@lichtblick/types';

type AutocompleteProps<TValue = string | number | undefined> = {
  isDisabled?: boolean;
  isSmall?: boolean;
  onRemoveItem?: (value?: Option<TValue>) => void;
  onRenderOption?: (option: Option<TValue>) => React.ReactNode;
  onSelect?: (value?: Option<TValue>[]) => void;
  options: Option<TValue>[] | undefined;
  placeholder?: string;
  setQuery: Dispatch<SetStateAction<string>>;
  values?: Option<TValue>[];
};

export const AutocompleteMultiselect = <TValue,>({
  isDisabled = false,
  isSmall,
  onRemoveItem,
  onRenderOption,
  onSelect,
  options,
  placeholder = 'Select',
  setQuery,
  values,
}: AutocompleteProps<TValue>) => {
  const ButtonRef = useRef<HTMLButtonElement>(null);
  const [pendingRemovalOption, setPendingRemovalOption] = useState<Option<TValue> | null>(null);
  const [isModifierToggledOn, setIsModifierToggledOn] = useState(false);

  const getOptionKey = (option: Option<TValue>, index: number): string => {
    const optionValue = option.value;

    if (optionValue !== undefined && optionValue !== null) {
      return String(optionValue);
    }

    return `${option.label}-${index}`;
  };

  const isSameOption = useCallback(
    (left: Option<TValue> | null | undefined, right: Option<TValue> | null | undefined): boolean => {
      if (!left || !right) {
        return false;
      }

      if (left.value !== undefined && left.value !== null && right.value !== undefined && right.value !== null) {
        return left.value === right.value;
      }

      return left.label === right.label;
    },
    [],
  );

  const truncateText = (text: string, maxLength: number = 40): string => {
    if (text.length <= maxLength) {
      return text;
    }

    return `${text.substring(0, maxLength)}...`;
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isComboboxActive = document.activeElement?.getAttribute('role') === 'combobox';

      if (event.altKey && isComboboxActive) {
        setIsModifierToggledOn((previousValue) => !previousValue);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const selectableOptions = useMemo(
    () =>
      (options ?? [])
        .filter((option) => !(values ?? []).some((selectedValue) => isSameOption(selectedValue, option)))
        .filter((option) => isModifierToggledOn || !(option as Option & { isDeleted?: boolean }).isDeleted)
        .sort((left, right) => {
          if (isModifierToggledOn) {
            return 0;
          }

          const leftDeleted = Boolean((left as Option & { isDeleted?: boolean }).isDeleted);
          const rightDeleted = Boolean((right as Option & { isDeleted?: boolean }).isDeleted);

          if (leftDeleted === rightDeleted) {
            return 0;
          }

          return leftDeleted ? 1 : -1;
        }),
    [isModifierToggledOn, isSameOption, options, values],
  );
  const displayedValues = values ?? [];

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !event.currentTarget.value && values?.length) {
      event.preventDefault();

      const currentIndex = values.findIndex((option) => isSameOption(option, pendingRemovalOption));
      const nextIndex =
        event.key === 'ArrowLeft'
          ? currentIndex > 0
            ? currentIndex - 1
            : values.length - 1
          : currentIndex >= 0
            ? (currentIndex + 1) % values.length
            : values.length - 1;

      setPendingRemovalOption(values[nextIndex] ?? null);

      return;
    }

    if (event.key === 'Backspace' && !event.currentTarget.value && values?.length) {
      event.preventDefault();

      if (pendingRemovalOption) {
        onRemoveItem?.(pendingRemovalOption);
        const currentIndex = values.findIndex((option) => isSameOption(option, pendingRemovalOption));
        const nextIndex = currentIndex > 0 ? currentIndex - 1 : 0;

        setPendingRemovalOption(values[nextIndex] ?? null);

        return;
      }

      setPendingRemovalOption(values[values.length - 1] ?? null);

      return;
    }

    setPendingRemovalOption(null);
  };

  return (
    <>
      <Combobox
        disabled={isDisabled}
        onChange={(option: Option<TValue> | null) => {
          setPendingRemovalOption(null);

          if (option && !values?.some((val) => isSameOption(val, option))) {
            const newValues = values ? [...values] : [];

            newValues.push(option);
            onSelect?.(newValues);
          }
        }}
      >
        {({ open: isOpen }) => (
          <Container>
            <AutocompleteTrigger $isSmall={!!isSmall}>
              <ChipContainer>
                {displayedValues.map((option, index) => (
                  <Chip
                    isHighlighted={!!pendingRemovalOption && isSameOption(pendingRemovalOption, option)}
                    key={getOptionKey(option, index)}
                    onCloseClick={() => onRemoveItem?.(option)}
                    size="small"
                    text={truncateText(option.label)}
                    variant="descriptive-turquoise"
                  />
                ))}
                <ComboboxInput
                  as={AutocompleteInput}
                  onBlur={() => {
                    setPendingRemovalOption(null);
                    setTimeout(() => setQuery(''), 500);
                  }}
                  onChange={(event) => {
                    if (event.target.value) {
                      setPendingRemovalOption(null);
                    }

                    setQuery(event.target.value);
                  }}
                  onClick={() => !isOpen && ButtonRef.current?.click()}
                  onKeyDown={handleInputKeyDown}
                  placeholder={values && values.length > 0 ? '' : placeholder}
                />
              </ChipContainer>
              <ComboboxButton as={AutocompleteButton} ref={ButtonRef}>
                {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </ComboboxButton>
            </AutocompleteTrigger>
            <ComboboxOptions as={AutocompleteOptions}>
              <OptionsContainer>
                {selectableOptions?.map?.((option, index) => {
                  const isDeleted = Boolean((option as Option & { isDeleted?: boolean }).isDeleted);

                  return (
                    <ComboboxOption as={AutocompleteOption} key={getOptionKey(option, index)} value={option}>
                      <AutocompleteItem
                        $isDeleted={isDeleted}
                        $isSelected={false}
                        data-deleted={isDeleted}
                        style={{
                          color: isDeleted ? 'var(--colors-gray-50)' : undefined,
                          opacity: isDeleted ? 0.6 : 1,
                          textDecoration: isDeleted ? 'line-through' : 'none',
                        }}
                      >
                        {(option as Option)?.icon}
                        {onRenderOption ? onRenderOption(option) : (option?.label ?? '')}
                      </AutocompleteItem>
                    </ComboboxOption>
                  );
                })}
              </OptionsContainer>
            </ComboboxOptions>
          </Container>
        )}
      </Combobox>
    </>
  );
};
